"use client"

import { useState, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useCart, formatEUR } from "@/lib/cart-store"
import type { MenuItem } from "@/lib/menu-data"

type Categoria = { id: string; nome: string; icone?: string }

export function MenuList({
  items,
  categorias,
  slug: _slug,
}: {
  items: MenuItem[]
  categorias: Categoria[]
  slug: string
}) {
  const [activeCat, setActiveCat] = useState<string>(categorias[0]?.id ?? "")
  const { addItem, lines } = useCart()
  const [pulseId, setPulseId] = useState<string | null>(null)

  const filtered = useMemo(
    () => items.filter((i) => i.categoria === activeCat),
    [items, activeCat]
  )

  const qtyOf = (id: string) => lines.find((l) => l.itemId === id)?.quantidade ?? 0

  const handleAdd = (item: MenuItem) => {
    addItem({ id: item.id, nome: item.nome, preco: item.preco, imagem: item.imagem })
    setPulseId(item.id)
    setTimeout(() => setPulseId(null), 350)
  }

  const activeName = categorias.find((c) => c.id === activeCat)?.nome ?? ""

  return (
    <div className="max-w-3xl mx-auto px-5 mt-6">
      {/* Sticky category bar — cream editorial. Móvil: scroll horizontal. Desktop: wrap centrado. */}
      <nav
        aria-label="Categorias da ementa"
        className="sticky top-[64px] z-20 -mx-5 px-4 sm:px-5 py-3 sm:py-4 flex gap-2.5 sm:gap-3 overflow-x-auto md:overflow-visible md:flex-wrap md:justify-center no-scrollbar hairline-b"
        style={{
          backgroundColor: "rgba(250, 246, 238, 0.94)",
          backdropFilter: "blur(12px)",
        }}
      >
        {categorias.map((c) => {
          const active = activeCat === c.id
          return (
            <button
              key={c.id}
              onClick={() => setActiveCat(c.id)}
              aria-pressed={active}
              className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-3 sm:px-6 sm:py-3 rounded-full text-sm sm:text-base font-semibold transition active:scale-[0.97]"
              style={{
                minHeight: 48,
                minWidth: 88,
                lineHeight: 1.1,
                letterSpacing: "0.01em",
                ...(active
                  ? {
                      backgroundColor: "var(--ph-burgundy)",
                      color: "var(--ph-bg)",
                      border: "1px solid var(--ph-burgundy)",
                      boxShadow: "0 2px 8px rgba(122, 31, 44, 0.18)",
                    }
                  : {
                      backgroundColor: "var(--ph-card)",
                      color: "var(--ph-ink)",
                      border: "1px solid var(--ph-hairline)",
                    }),
              }}
            >
              {c.icone && (
                <span aria-hidden style={{ fontSize: "1.1em", lineHeight: 1 }}>
                  {c.icone}
                </span>
              )}
              <span>{c.nome}</span>
            </button>
          )
        })}
      </nav>

      {/* Section heading */}
      <div className="mt-8 mb-5 flex items-center gap-3">
        <div
          className="h-px flex-1"
          style={{ background: "linear-gradient(to right, transparent, var(--ph-hairline), transparent)" }}
        />
        <h2
          className="text-[11px] uppercase tracking-[0.3em] font-bold"
          style={{ color: "var(--ph-burgundy)" }}
        >
          {activeName}
        </h2>
        <div
          className="h-px flex-1"
          style={{ background: "linear-gradient(to right, transparent, var(--ph-hairline), transparent)" }}
        />
      </div>

      <div className="space-y-3">
        <AnimatePresence mode="popLayout">
          {filtered.map((item, i) => {
            const qty = qtyOf(item.id)
            const hasMeia = typeof item.precoMeia === "number"
            const hasMedio = typeof item.precoMedio === "number"
            const hasFamilia = typeof item.precoFamilia === "number"
            const hasSizes = hasMedio || hasFamilia
            const isWine = item.categoria === "vinhos-tintos"

            return (
              <motion.article
                key={item.id}
                layout
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ delay: Math.min(i * 0.03, 0.25), duration: 0.3 }}
                className="rounded-2xl overflow-hidden flex transition-colors"
                style={{
                  backgroundColor: "var(--ph-card)",
                  border: "1px solid var(--ph-hairline)",
                  boxShadow: "0 1px 2px rgba(31, 20, 16, 0.04)",
                }}
              >
                <div className="flex-1 p-4 min-w-0">
                  <div className="flex items-start gap-2">
                    <h3
                      className="font-fraunces italic font-bold text-[17px] leading-tight"
                      style={{ color: "var(--ph-ink)" }}
                    >
                      {item.nome}
                    </h3>
                    {item.popular && (
                      <span
                        className="shrink-0 text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded"
                        style={{
                          backgroundColor: "var(--ph-burgundy)",
                          color: "var(--ph-bg)",
                        }}
                      >
                        Top
                      </span>
                    )}
                  </div>

                  {/* Wine metadata: region · grapes */}
                  {isWine && (item.regiao || item.castas) && (
                    <p
                      className="mono-label mt-1"
                      style={{ fontSize: "9px", letterSpacing: "0.18em" }}
                    >
                      {[item.regiao, item.castas].filter(Boolean).join(" · ")}
                    </p>
                  )}

                  {/* Description (skip for wines — castas already shown) */}
                  {!isWine && item.descricao && (
                    <p
                      className="text-[12.5px] mt-1.5 leading-relaxed line-clamp-2"
                      style={{ color: "var(--ph-ink-muted)" }}
                    >
                      {item.descricao}
                    </p>
                  )}

                  {/* Pizza sizes block — when item has precoMedio/precoFamilia */}
                  {hasSizes && (
                    <div
                      className="mt-2.5 grid grid-cols-3 gap-2 rounded-lg p-2"
                      style={{
                        backgroundColor: "rgba(122, 31, 44, 0.04)",
                        border: "1px solid var(--ph-hairline)",
                      }}
                    >
                      <div className="text-center">
                        <div
                          className="mono-label"
                          style={{ fontSize: "9px", letterSpacing: "0.14em", color: "var(--ph-ink-muted)" }}
                        >
                          PEQUENA · 28cm
                        </div>
                        <div
                          className="font-fraunces font-bold text-[15px] leading-none mt-1"
                          style={{ color: "var(--ph-ink)" }}
                        >
                          {formatEUR(item.preco)}
                        </div>
                      </div>
                      <div
                        className="text-center"
                        style={{ borderLeft: "1px solid var(--ph-hairline)", borderRight: hasFamilia ? "1px solid var(--ph-hairline)" : "none" }}
                      >
                        <div
                          className="mono-label"
                          style={{ fontSize: "9px", letterSpacing: "0.14em", color: "var(--ph-ink-muted)" }}
                        >
                          MÉDIA · 32cm
                        </div>
                        <div
                          className="font-fraunces font-bold text-[15px] leading-none mt-1"
                          style={{ color: hasMedio ? "var(--ph-ink)" : "var(--ph-ink-muted)" }}
                        >
                          {hasMedio ? formatEUR(item.precoMedio!) : "—"}
                        </div>
                      </div>
                      <div className="text-center">
                        <div
                          className="mono-label"
                          style={{ fontSize: "9px", letterSpacing: "0.14em", color: "var(--ph-ink-muted)" }}
                        >
                          FAMÍLIA · 50cm
                        </div>
                        <div
                          className="font-fraunces font-bold text-[15px] leading-none mt-1"
                          style={{ color: hasFamilia ? "var(--ph-ink)" : "var(--ph-ink-muted)" }}
                        >
                          {hasFamilia ? formatEUR(item.precoFamilia!) : "—"}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-3 flex items-center justify-between gap-3">
                    {/* Price block — hidden when sizes block shown */}
                    {!hasSizes && (
                      <div className="flex items-baseline gap-2 flex-wrap">
                        {hasMeia && (
                          <span
                            className="text-[12px] font-medium"
                            style={{ color: "var(--ph-ink-muted)" }}
                          >
                            ½ {formatEUR(item.precoMeia!)}
                          </span>
                        )}
                        <span
                          className="font-fraunces font-bold text-[20px] leading-none"
                          style={{ color: "var(--ph-ink)" }}
                        >
                          {formatEUR(item.preco)}
                        </span>
                        {item.unidade && (
                          <span
                            className="text-[11px] font-medium"
                            style={{ color: "var(--ph-ink-muted)" }}
                          >
                            /{item.unidade}
                          </span>
                        )}
                      </div>
                    )}
                    {hasSizes && <div />}

                    <motion.button
                      onClick={() => handleAdd(item)}
                      animate={pulseId === item.id ? { scale: [1, 1.15, 1] } : {}}
                      transition={{ duration: 0.3 }}
                      className="text-[11px] font-bold uppercase tracking-[0.12em] px-3.5 py-2 rounded-full transition flex items-center gap-1.5 shrink-0"
                      style={
                        qty > 0
                          ? {
                              backgroundColor: "var(--ph-ink)",
                              color: "var(--ph-bg)",
                            }
                          : {
                              backgroundColor: "var(--ph-burgundy)",
                              color: "var(--ph-bg)",
                            }
                      }
                    >
                      {qty > 0 ? (
                        <>
                          <span
                            className="rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold"
                            style={{
                              backgroundColor: "var(--ph-gold)",
                              color: "var(--ph-ink)",
                            }}
                          >
                            {qty}
                          </span>
                          <span>Adicionar</span>
                        </>
                      ) : (
                        <span>+ Adicionar</span>
                      )}
                    </motion.button>
                  </div>
                </div>

                {/* Image — hidden for wines (no plate photo makes sense) */}
                {!isWine && item.imagem && (
                  <div
                    className="w-28 sm:w-36 shrink-0 bg-cover bg-center relative"
                    style={{ backgroundImage: `url(${item.imagem})` }}
                    aria-hidden
                  >
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(to left, transparent 60%, rgba(255,255,255,0.35))",
                      }}
                    />
                  </div>
                )}
              </motion.article>
            )
          })}
        </AnimatePresence>
      </div>

      {/* Footer note */}
      <p
        className="mt-10 mb-6 text-[11px] text-center"
        style={{ color: "var(--ph-ink-muted)" }}
      >
        Todos os preços incluem IVA à taxa em vigor.
        <br />
        Pratos confecionados na hora, podem estar sujeitos a demora.
      </p>
    </div>
  )
}
